import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

type SearchPage<TResult> = { results: TResult[]; totalCount: number };

/**
 * A search list on phones: the same page-based request at the same page size, loaded page after
 * page as the list scrolls and stitched together here. Key it under the list's existing query
 * root, so whatever refreshes the paged list after a save refreshes every loaded page too.
 */
export const useSearchPages = <TResult>({
  queryKey,
  fetchPage,
  enabled = true,
}: {
  queryKey: readonly unknown[];
  fetchPage: (pageNumber: number) => Promise<SearchPage<TResult>>;
  enabled?: boolean;
}) => {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((total, page) => total + page.results.length, 0);
      const hasMore = lastPage.results.length > 0 && loadedCount < (allPages[0]?.totalCount ?? 0);
      return hasMore ? allPages.length : undefined;
    },
    enabled,
  });

  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.results) ?? [], [query.data]);

  return {
    rows,
    // The first page's total: later pages aren't guaranteed to carry one.
    totalCount: query.data?.pages[0]?.totalCount ?? 0,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
